import { raf } from 'utils/eventHelpers';
import { setPrefixedStyle } from 'utils/DOMHelpers';

class CarouselProducts {
  constructor(element, options) {
    this._element = element;
    this._options = Object.assign(CarouselProducts.options, options);

    this._refs = {
      window: element.querySelector(this._options.window),
      products: [].slice.call(element.querySelectorAll(this._options.products)),
      previous: [].slice.call(element.querySelectorAll(this._options.previous)),
      next: [].slice.call(element.querySelectorAll(this._options.next)),
      slides: [],
      body: document.querySelector('body'),
    };

    this._refs.slides = [].slice.call(this._refs.window.children);
    this._refs.window.scrollLeft = 0;
    this._element.setAttribute('data-initialized', 'true');

    this._state = {
      index: 0,
      dragging: false,
      currentPos: 0,
      maxPos: 1,
      direction: 'right',
      touchStart: {
        x: 0,
        y: 0,
      },
    };

    this.goto(0);
  }

  static options = {
    bullet: null,
    window: null,
    products: null,
    left: null,
    right: null,
    threshold: 30,
    bound: 0.66,
    breakpoint: 720,
    minmax: {
      min: 1,
      max: 3,
    },
    timeout: 300,
  };

  _addEventListeners(count) {
    if (!count) {
      return;
    }
    this._onTouchMove = raf(this._onTouchMove.bind(this));

    this._refs.window.addEventListener('mousedown', (e) => this._onTouchStart(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchstart', (e) => this._onTouchStart(e), {
      passive: true,
    });
    this._refs.previous.forEach((previous) => {
      previous.addEventListener('click', () => this.goto(this._state.index - 1));
      previous.addEventListener('touchend', () => this.goto(this._state.index - 1));
    });

    this._refs.next.forEach((next) => {
      next.addEventListener('click', () => this.goto(this._state.index + 1));
      next.addEventListener('touchend', () => this.goto(this._state.index + 1));
    });

    this._refs.window.addEventListener('mousemove', (e) => this._onTouchMove(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchmove', (e) => this._onTouchMove(e), {
      passive: true,
    });
    this._refs.window.addEventListener('touchend', (e) => this._onTouchEnd(e), {
      passive: true,
    });
    document.addEventListener('mouseup', (e) => this._onTouchEnd(e), {
      passive: true,
    });

    this._refs.slides.forEach((slide) =>
      slide.addEventListener('dragstart', (e) => e.preventDefault())
    );
  }

  get slideWidth() {
    return this._refs.slides[0].offsetWidth;
  }

  // eslint-disable-next-line accessor-pairs
  set activeProduct(index) {
    this._refs.products.forEach((product, i) => {
      product.setAttribute('data-active', index === i);
      if (index === i) {
        this._initProductCarousel(product);
      }
    });
  }

  _initProductCarousel(product) {
    let maxItems = this._options.minmax.min;
    if (this._refs.body.offsetWidth > this._options.breakpoint) {
      maxItems = this._options.minmax.max;
    }
    const count = product.parentNode.childElementCount > maxItems;
    product.parentNode.parentNode.setAttribute('data-navigation', count);
    this._refs.window.setAttribute('data-carousel-active', count);
    this._addEventListeners(count);
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this._initProductCarousel(product);
      }, this._options.timeout);
    });
  }

  _onTouchStart(e) {
    if (this._state.dragging) return;
    this._state.dragging = true;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    this._state.touchStart = {
      x: pointer.pageX,
      y: pointer.pageY,
    };
  }
  // eslint-disable-next-line complexity
  _onTouchMove(e) {
    if (!this._state.dragging) return;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    const d = {
      x: pointer.pageX - this._state.touchStart.x,
      y: pointer.pageY - this._state.touchStart.y,
    };

    if (Math.abs(d.x) > Math.abs(d.y)) {
      const offset = -this._state.index * this.slideWidth + d.x;
      const isWithinBounds =
        offset >= this.slideWidth * -(this._refs.slides.length - 1) && offset <= 0;
      const dir = offset / Math.abs(offset);
      const target = isWithinBounds
        ? offset
        : offset - d.x * this._options.bound + dir * Math.sqrt(Math.abs(d.x) * this._options.bound);

      this._refs.slides.forEach((slide) => {
        setPrefixedStyle(slide, 'transform', `translateX(${target}px)`);
        setPrefixedStyle(slide, 'transition-duration', '.1s');
      });
    } else {
      this.slideTo(this.index);
    }
  }
  // eslint-disable-next-line complexity
  _onTouchEnd(e) {
    if (!this._state.dragging) return;
    this._state.dragging = false;

    const pointer = e.changedTouches ? e.changedTouches[0] : e;
    const d = {
      x: pointer.pageX - this._state.touchStart.x,
      y: pointer.pageY - this._state.touchStart.y,
    };

    if (Math.abs(d.x) > Math.abs(d.y) && Math.abs(d.x) > this._options.threshold) {
      // eslint-disable-next-line no-unused-expressions
      d.x > 0 ? this.previous() : this.next();
    } else {
      this.slideTo(this._state.index);
    }
  }

  goto(index) {
    const max = this._refs.slides.length - 1;
    const i = Math.min(Math.max(index, 0), max);

    this._refs.slides[this._state.index].removeAttribute('data-active');
    this._refs.slides[i].setAttribute('data-active', 'true');

    this.slideTo(i);
    this.activeProduct = i;
    this._state.index = i;
    this._refs.previous[0].setAttribute('data-active', this._state.index > 1);
    this._refs.next[0].setAttribute('data-active', this._state.index < this._state.maxPos);
  }

  previous() {
    this.goto(this._state.index - 1);
  }

  next() {
    this.goto(this._state.index + 1);
  }

  slideTo(index) {
    const target = -index * this.slideWidth;
    // eslint-disable-next-line no-unused-expressions
    target <= this._state.currentPos
      ? (this._state.direction = 'left')
      : (this._state.direction = 'right');
    this._state.currentPos = target;
    this._refs.slides.forEach((slide) => {
      setPrefixedStyle(slide, 'transform', `translateX(${target}px)`);
      setPrefixedStyle(slide, 'transition', '');
    });
  }
}

export default CarouselProducts;
